export const createAgentOption = (digit = '1') => ({
  digit,
  label: '',
  target: { type: 'agent', agent_id: '' },
})

export const createPhoneNumberOption = (digit = '1') => ({
  digit,
  label: '',
  target: { type: 'phone_number', phone_number: '' },
})

export const createSubmenuOption = (digit = '1') => ({
  digit,
  label: '',
  target: {
    type: 'submenu',
    prompt: '',
    options: [createAgentOption('1')],
  },
})

export const createEmptyIvrConfig = () => ({
  version: 1,
  prompt: '',
  speed: 1.25,
  timeout_seconds: 10,
  retries: 2,
  no_input_message: 'We did not receive a selection.',
  invalid_input_message: 'That selection is not valid.',
  options: [createAgentOption('1')],
})

const clone = (value) => JSON.parse(JSON.stringify(value))
const PHONE_NUMBER_PATTERN = /^(?:\d{10}|\+91\d{10})$/
const IVR_SPEED_MIN = 0.6
const IVR_SPEED_MAX = 1.5
const IVR_SPEED_DEFAULT = 1.25

const normalizeSpeed = (value) => {
  if (value === null || value === undefined || value === '') return IVR_SPEED_DEFAULT
  const speed = Number(value)
  if (!Number.isFinite(speed)) return IVR_SPEED_DEFAULT
  return Math.min(IVR_SPEED_MAX, Math.max(IVR_SPEED_MIN, speed))
}

const normalizeTarget = (value, digit = '1') => {
  const source = value && typeof value === 'object' ? value : {}

  if (source.type === 'submenu') {
    const options = Array.isArray(source.options) && source.options.length
      ? source.options
      : [createAgentOption('1')]

    return {
      ...createSubmenuOption(digit).target,
      ...source,
      options: options.map((option, index) => ({
        ...createAgentOption(String(index + 1)),
        ...option,
        target: normalizeTarget(option.target, String(index + 1)),
      })),
    }
  }

  if (source.type === 'phone_number') {
    return {
      ...createPhoneNumberOption(digit).target,
      ...source,
    }
  }

  return {
    ...createAgentOption(digit).target,
    ...source,
    type: 'agent',
  }
}

export const normalizeIvrConfig = (value) => {
  const source = value && typeof value === 'object' ? value : {}
  const defaults = createEmptyIvrConfig()
  const options = Array.isArray(source.options) && source.options.length
    ? source.options
    : defaults.options
  return {
    ...defaults,
    ...source,
    speed: normalizeSpeed(source.speed),
    options: options.map((option, index) => ({
      ...createAgentOption(String(index + 1)),
      ...option,
      target: normalizeTarget(option.target, String(index + 1)),
    })),
  }
}

const validateTarget = (target, prefix, errors, allowSubmenu = true) => {
  if (target.type === 'agent') {
    if (!String(target.agent_id || '').trim()) errors[`${prefix}.agent_id`] = 'Select an agent.'
    return
  }

  if (target.type === 'phone_number') {
    const phoneNumber = String(target.phone_number || '').trim()
    if (!phoneNumber) {
      errors[`${prefix}.phone_number`] = 'Enter a phone number.'
    } else if (!PHONE_NUMBER_PATTERN.test(phoneNumber)) {
      errors[`${prefix}.phone_number`] = 'Use 10 digits or +91 followed by 10 digits.'
    }
    return
  }

  if (target.type !== 'submenu') {
    errors[`${prefix}.target`] = 'Select a routing target.'
    return
  }
  if (!allowSubmenu) {
    errors[`${prefix}.target`] = 'Submenus can only be one level deep.'
    return
  }

  if (!String(target.prompt || '').trim()) errors[`${prefix}.submenu_prompt`] = 'Enter the submenu message.'
  if (!Array.isArray(target.options) || !target.options.length) errors[`${prefix}.submenu_options`] = 'Add a submenu option.'

  const submenuDigits = new Set()
  ;(target.options || []).forEach((subOption, subIndex) => {
    const subPrefix = `${prefix}.submenu.${subIndex}`
    if (!/^\d$/.test(String(subOption.digit || ''))) errors[`${subPrefix}.digit`] = 'Use one digit.'
    if (submenuDigits.has(String(subOption.digit))) errors[`${subPrefix}.digit`] = 'Digits must be unique.'
    submenuDigits.add(String(subOption.digit))
    validateTarget(subOption.target || {}, subPrefix, errors, false)
  })
}

export const validateIvrConfig = (value) => {
  const config = normalizeIvrConfig(value)
  const errors = {}
  if (config.version !== 1) errors.version = 'Version must be 1.'
  if (!String(config.prompt || '').trim()) errors.prompt = 'Enter the root menu message.'
  if (!String(config.no_input_message || '').trim()) errors.no_input_message = 'Enter the no-input message.'
  if (!String(config.invalid_input_message || '').trim()) errors.invalid_input_message = 'Enter the invalid-digit message.'
  if (!Number.isInteger(config.timeout_seconds) || config.timeout_seconds < 5 || config.timeout_seconds > 15) {
    errors.timeout_seconds = 'Use a whole number from 5 to 15.'
  }
  if (!Number.isInteger(config.retries) || config.retries < 0 || config.retries > 3) {
    errors.retries = 'Use a whole number from 0 to 3.'
  }
  if (!Array.isArray(config.options) || !config.options.length) errors.options = 'Add at least one option.'
  if (Array.isArray(config.options) && config.options.length > 10) errors.options = 'Use at most 10 options.'
  const digits = new Set()
  config.options.forEach((option, index) => {
    const prefix = `options.${index}`
    if (!/^\d$/.test(String(option.digit || ''))) errors[`${prefix}.digit`] = 'Use one digit from 0 to 9.'
    if (digits.has(String(option.digit))) errors[`${prefix}.digit`] = 'Digits must be unique within this menu.'
    digits.add(String(option.digit))
    validateTarget(option.target || {}, prefix, errors)
  })
  return errors
}

export const updateIvrConfig = (value, updater) => {
  const next = clone(normalizeIvrConfig(value))
  updater(next)
  return next
}
